import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import Label from "../../../../components/form_fields/Label";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { useReinstateEmployee } from "../../../../services/employee.service";
import { EmployeeReinstatementDto } from "../../../../models/employee.dto";

interface IReinstateEmployee {
	open: boolean;
	setOpen: (val: boolean) => void;
	reloadTableData: () => void;
}

interface IFormInputs {
	employmentDate: string;
}

const ReinstateEmployee = ({
	open,
	setOpen,
	reloadTableData,
}: IReinstateEmployee) => {
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);
	const { mutate: reinstateEmployee } = useReinstateEmployee();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>();

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		const payload: EmployeeReinstatementDto = {
			reemployment: true,
			employmentDate: data.employmentDate,
			status: "ACTIVE" as any,
		};

		reinstateEmployee(
			{ payload, id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					handleClose();
				},
				onSettled: () => setUploading(false),
			},
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<h2 className="font-semibold text-base">
					Reinstate{" "}
					<span className="text-primary-blue">
						{formDataValues?.firstName} {formDataValues?.lastName}
					</span>
				</h2>
			</DialogTitle>
			<DialogContent>
				<p className="text-sm text-gray-500 mb-6">
					This will reinstate the employee and set their status back to{" "}
					<span className="font-semibold text-gray-700">Active</span>. Please
					provide a new employment date to continue.
				</p>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="w-full mb-6">
						<Label title="New Employment Date" for="employmentDate" />
						<input
							type="date"
							{...register("employmentDate", {
								required: "Please provide a new employment date",
							})}
							id="employmentDate"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.employmentDate
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.employmentDate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.employmentDate.message}
							</p>
						)}
					</div>

					<div className="flex w-full justify-end space-x-4 items-center mt-4">
						<button
							type="button"
							onClick={handleClose}
							className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
						>
							Cancel
						</button>
						<OrangeButton
							title={uploading ? "Reinstating..." : "Reinstate Employee"}
							className="px-8"
							type="submit"
							disabled={uploading}
						/>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ReinstateEmployee;
